import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { adminGetAllTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial } from '../api/api';

export const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    rating: 5,
    text: '',
    avatar: '',
    is_visible: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const data = await adminGetAllTestimonials();
      setTestimonials(data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        name: testimonial.name,
        company: testimonial.company,
        rating: testimonial.rating,
        text: testimonial.text,
        avatar: testimonial.avatar,
        is_visible: testimonial.is_visible
      });
    } else {
      setEditingTestimonial(null);
      setFormData({
        name: '',
        company: '',
        rating: 5,
        text: '',
        avatar: '',
        is_visible: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTestimonial(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTestimonial) {
        await adminUpdateTestimonial(editingTestimonial.id, formData);
        toast.success('Témoignage mis à jour');
      } else {
        await adminCreateTestimonial(formData);
        toast.success('Témoignage créé');
      }
      
      handleCloseDialog();
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce témoignage ?')) return;

    try {
      await adminDeleteTestimonial(id);
      toast.success('Témoignage supprimé');
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des témoignages</h2>
          <p className="text-gray-600">Avis clients sur votre site</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-600">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau témoignage
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id} className={`border-0 shadow-lg ${!testimonial.is_visible ? 'opacity-50' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-600 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="text-white font-bold">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{testimonial.name}</h3>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => handleOpenDialog(testimonial)} className="p-2 bg-blue-50 rounded-lg hover:bg-blue-100">
                    <Edit className="w-4 h-4 text-blue-600" />
                  </button>
                  <button onClick={() => handleDelete(testimonial.id)} className="p-2 bg-red-50 rounded-lg hover:bg-red-100">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>

              <div className="flex space-x-1 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>

              <p className="text-gray-700 text-sm italic mb-3">"{testimonial.text}"</p>
              
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <span className={`px-2 py-1 rounded ${testimonial.is_visible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {testimonial.is_visible ? 'Visible' : 'Masqué'}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTestimonial ? 'Modifier le témoignage' : 'Nouveau témoignage'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Entreprise/Fonction *</Label>
                <Input id="company" value={formData.company} onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="avatar">Initiales (ex: JD) *</Label>
                <Input id="avatar" value={formData.avatar} onChange={(e) => setFormData(prev => ({ ...prev, avatar: e.target.value.toUpperCase() }))} maxLength={2} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rating">Note</Label>
                <select id="rating" value={formData.rating} onChange={(e) => setFormData(prev => ({ ...prev, rating: parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
                  {[5, 4, 3, 2, 1].map(r => (
                    <option key={r} value={r}>{r} étoile{r > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="text">Témoignage *</Label>
              <Textarea id="text" value={formData.text} onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))} required rows={4} />
            </div>

            <div className="flex items-center space-x-2">
              <Switch id="visible" checked={formData.is_visible} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_visible: checked }))} />
              <Label htmlFor="visible">Visible sur le site</Label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Annuler</Button>
              <Button type="submit" className="bg-blue-600">{editingTestimonial ? 'Mettre à jour' : 'Créer'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
